import type { PermissionView } from '@classgo/shared';
import { Badge, Checkbox } from '@classgo/ui';

import type { messages } from '../../i18n';
import { useT } from '../../i18n';
import type { PermissionSource } from './types';

type Key = keyof typeof messages;

/**
 * The Spanish label of a permission key, falling back to the key when the catalog grew before
 * the messages did.
 * @param {ReturnType<typeof useT>} t -- The translator.
 * @param {string} key -- The permission key.
 * @returns {string} The label.
 */
export const permissionLabel = (t: ReturnType<typeof useT>, key: string): string => {
  const messageKey = `permission.${key}` as Key;
  const text = t(messageKey);
  return text === messageKey ? key : text;
};

/**
 * The Spanish name of a resource (`billing` → "Cobros").
 * @param {ReturnType<typeof useT>} t -- The translator.
 * @param {string} resource -- The resource half of the key.
 * @returns {string} The name.
 */
export const resourceLabel = (t: ReturnType<typeof useT>, resource: string): string => {
  const messageKey = `resource.${resource}` as Key;
  const text = t(messageKey);
  return text === messageKey ? resource : text;
};

/**
 * Groups the catalog by resource, keeping the catalog's order inside each group.
 * @param {PermissionView[]} catalog -- The permissions.
 * @returns The groups.
 */
export const groupByResource = (
  catalog: readonly PermissionView[],
): { resource: string; items: PermissionView[] }[] => {
  const groups = new Map<string, PermissionView[]>();
  for (const item of catalog) {
    groups.set(item.resource, [...(groups.get(item.resource) ?? []), item]);
  }
  return [...groups.entries()].map(([resource, items]) => ({ resource, items }));
};

/**
 * The catalog as a list of checkboxes grouped by resource, in plain Spanish. With `sources`, each
 * row also says where the permission comes from (the template, an exception, or nowhere), which
 * is what makes an exception readable.
 */
export const PermissionPicker = ({
  catalog,
  selected,
  onToggle,
  sources,
  disabled,
}: {
  catalog: readonly PermissionView[];
  selected: ReadonlySet<string>;
  onToggle: (key: string, checked: boolean) => void;
  sources?: ReadonlyMap<string, PermissionSource>;
  disabled?: boolean;
}) => {
  const t = useT();
  const tone: Record<PermissionSource, 'gray' | 'success' | 'danger' | 'info'> = {
    template: 'info',
    added: 'success',
    removed: 'danger',
    none: 'gray',
  };
  return (
    <div className="space-y-4">
      {groupByResource(catalog).map((group) => (
        <fieldset key={group.resource} className="rounded-card border border-gray-200 p-3">
          <legend className="px-1 text-sm font-semibold text-gray-900">
            {resourceLabel(t, group.resource)}
          </legend>
          <div className="mt-1 space-y-1">
            {group.items.map((item) => {
              const source = sources?.get(item.key);
              return (
                <div key={item.key} className="flex flex-wrap items-center gap-2">
                  <Checkbox
                    label={permissionLabel(t, item.key)}
                    description={item.key}
                    checked={selected.has(item.key)}
                    disabled={disabled ?? false}
                    onChange={(event) => onToggle(item.key, event.target.checked)}
                  />
                  {source && source !== 'none' ? (
                    <Badge tone={tone[source]}>{t(`users.source.${source}` as Key)}</Badge>
                  ) : null}
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
};
